import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Avatar } from "@/components/shared/Avatar";
import { FormSection } from "@/components/shared/FormSection";
import { useIsMobile, useIsNarrow } from "@/components/shared/PagePrimitives";
import { ListHeader, ListToolbar } from "@/components/shared/ListToolbar";
import { useListFilters } from "@/hooks/useListFilters";
import { useAuth } from "@/context/AuthContext";
import { currencySymbol, formatCZK, formatDateTime, plur } from "@/lib/format";
import { storage } from "@/lib/storage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, Link2, Copy, Send, AlertTriangle, User, Mail, Briefcase, LayoutGrid, Table2, Users, CaseSensitive, Clock, HardHat, FileSignature } from "lucide-react";
import { useGroupSort, GroupedGrid, GroupHeader } from "@/components/shared/GroupSort";
import { ContractPicker, contractOf } from "@/components/team/ContractPicker";
import { toast } from "sonner";

const ROLE_OPTIONS = [
  { value: "MANAGER", label: "Stavbyvedoucí" },
  { value: "WORKER", label: "Dělník" },
  { value: "SUBCONTRACTOR", label: "Subdodavatel" },
  { value: "ACCOUNTANT", label: "Účetní" },
  { value: "ASSISTANT", label: "Asistentka" },
  { value: "WAREHOUSE", label: "Skladník" },
  { value: "DESIGNER", label: "Projektant" },
];

const ROLES_WITH_POSITION = ["WORKER", "SUBCONTRACTOR"];

const ROLE_HINT = {
  MANAGER: "Stavbyvedoucí vidí své zakázky, deník, docházku i rozpočet.",
  WORKER: "Dělník vidí jen svou docházku a úkoly. Pozice je jeho profese na stavbě.",
  SUBCONTRACTOR: "Subdodavatel vidí jen zakázky, na kterých pracuje. Pozice je jeho řemeslo. Je placený od úkolu — místo sazby nahrává faktury, které schvaluje vedení.",
  ACCOUNTANT: "Účetní vidí ekonomiku a doklady, ne stavební část.",
  ASSISTANT: "Asistentka spravuje doklady, tým a zakázky, nevidí mzdové sazby.",
  WAREHOUSE: "Skladník vidí všechny sklady i ceny, naskladňuje doklady, dělá příjemky a inventury. Nevidí ekonomiku.",
  DESIGNER: "Projektant vidí jen zakázky, kam je přiřazený: informace, harmonogram, Gantt, deník, docházku bez peněz, fotky, pasport a tým. Píše deník, schvaluje a maže fotky a spravuje úkoly. Neeviduje docházku a nikde nevidí peníze.",
  OWNER: "Majitel má plná práva: sazby, mzdové náklady, ekonomika, fakturace i správa týmu. Dalšího majitele může pozvat jen majitel.",
};

const ROLE_LABELS = { OWNER: "Majitel", ...Object.fromEntries(ROLE_OPTIONS.map((r) => [r.value, r.label])) };

// Čtyři stavy, které uživatel rozeznává. Server drží jen `status` + `invite_email_sent_at`.
const inviteState = (u) =>
  u.status === "inactive" ? "inactive"
  : u.status === "active" ? "active"
  : u.invite_email_sent_at ? "invited" : "none";

const STATE = {
  active: { label: "aktivní", cls: "bg-success/15 text-success" },
  invited: { label: "pozván", cls: "bg-amber-500/15 text-amber-600 dark:text-amber-400" },
  none: { label: "bez pozvánky", cls: "bg-sky-500/15 text-sky-600 dark:text-sky-400" },
  inactive: { label: "neaktivní", cls: "bg-secondary text-muted-foreground" },
};

// 354: jednotná lišta — záložky Všichni / Bez pozvánky / Pozvaní / Neaktivní (dřív dlaždice), Filtr Role,
// pohledy Tabulka (jen na šířku) / Karty, Pozvat člena (N).
// 378: Seskupit a řadit (jako Doklady) — skupiny Role / Pozice / Stav pozvánky / Úvazek, řazení Jméno / Role / Naposledy přihlášen.
const ROLE_ORDER = ["OWNER", ...ROLE_OPTIONS.map((r) => r.value)];
const STATE_ORDER = ["active", "invited", "none", "inactive"];
const cap = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : t);
const byName = (a, b) => (a.name || "").localeCompare(b.name || "", "cs");
const TEAM_ORDERS = {
  name: { label: "Jméno", icon: CaseSensitive, dirs: ["A → Z", "Z → A"], def: "asc", val: (u) => u.name || "" },
  role: { label: "Role", icon: Briefcase, dirs: ["Vedení první", "Obráceně"], def: "asc", val: (u) => ROLE_ORDER.indexOf(u.role) + 1 },
  login: { label: "Naposledy přihlášen", icon: Clock, dirs: ["Nejdávněji", "Nejnověji"], def: "desc", val: (u) => u.last_login || "" },
};

const docsWarnTitle = (w) => {
  const parts = [];
  if (w.expired) parts.push(`${w.expired} po platnosti`);
  if (w.expiring) parts.push(`${w.expiring} brzy vyprší`);
  return `Dokumenty: ${parts.join(", ")}`;
};

const StatePill = ({ u, testid }) => {
  const s = STATE[inviteState(u)];
  return <span data-testid={testid} className={`inline-flex items-center text-xs font-bold px-2.5 py-0.5 rounded-full whitespace-nowrap ${s.cls}`}>{s.label}</span>;
};

export default function Team() {
  const { user } = useAuth();
  const isOwner = user?.role === "OWNER";
  const canManage = isOwner || user?.role === "ASSISTANT";
  // 179: stavbyvedoucí smí přidat dělníka / subdodavatele a dát mu sazbu.
  const isSiteMgr = user?.role === "MANAGER";
  const canInvite = canManage || isSiteMgr;
  const roleOptions = isOwner ? [{ value: "OWNER", label: "Majitel" }, ...ROLE_OPTIONS]
    : isSiteMgr ? ROLE_OPTIONS.filter((r) => ["WORKER", "SUBCONTRACTOR"].includes(r.value)) : ROLE_OPTIONS;

  const [team, setTeam] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "WORKER", phone: "", position: "", hourly_rate: "", contract: "HPP", send_email: false });
  const [linkDialog, setLinkDialog] = useState({ open: false, url: "", email: "", uid: null, sent: false });
  const [busy, setBusy] = useState(false);

  const [view, setViewState] = useState(() => (storage.get("team_view") === "cards" ? "cards" : "table"));
  const setView = (v) => { setViewState(v); storage.set("team_view", v); };
  const [q, setQ] = useState("");
  const [stateF, setStateF] = useState("all");
  const nav = useNavigate();

  const load = () => api.get("/team").then((r) => setTeam(r.data));
  useEffect(() => { load(); }, []);

  const kpi = useMemo(() => {
    let total = 0, none = 0, invited = 0, inactive = 0;
    team.forEach((u) => {
      const st = inviteState(u);
      if (st === "inactive") inactive++; else total++;
      if (st === "none") none++;
      if (st === "invited") invited++;
    });
    return { total, none, invited, inactive };
  }, [team]);

  // Množina podle stavu (bez role/hledání) — z ní se počítají pilulky rolí.
  const base = useMemo(() => team.filter((u) => {
    const st = inviteState(u);
    return stateF === "all" ? st !== "inactive" : st === stateF;
  }), [team, stateF]);

  const mobile = useIsMobile();
  const narrow = useIsNarrow();   // telefon a tablet na výšku: jen Karty (tabulka by přetékala)
  const groups = useMemo(() => [{ key: "role", label: "Role", icon: Briefcase,
    options: (isOwner ? [{ value: "OWNER", label: "Majitel" }] : []).concat(ROLE_OPTIONS),
    match: (u, v) => u.role === v }], [isOwner]);
  const F = useListFilters(base, {
    groups, searchTerm: q, entity: { one: "člen", few: "členové", many: "členů" },
    search: (u) => `${u.name || ""} ${ROLE_LABELS[u.role] || ""} ${u.position || ""} ${u.email || ""}`,
  });

  // Sloupec sazby jen když ho server aspoň u někoho poslal (kdo sazby nevidí, pole nemá).
  const showRateCol = useMemo(() => team.some((u) => u.hourly_rate != null), [team]);
  const teamGroups = useMemo(() => ({
    role: { label: "Role", icon: Briefcase, key: (u) => u.role, name: (k) => ROLE_LABELS[k] || k, order: (a, b) => ROLE_ORDER.indexOf(a) - ROLE_ORDER.indexOf(b), dirs: ["Vedení první", "Obráceně"] },
    position: { label: "Pozice", icon: HardHat, key: (u) => (u.position || "").trim(), none: "Bez pozice" },
    state: { label: "Stav pozvánky", icon: Send, key: inviteState, name: (k) => cap(STATE[k]?.label), order: (a, b) => STATE_ORDER.indexOf(a) - STATE_ORDER.indexOf(b), dirs: ["Aktivní první", "Neaktivní první"] },
    ...(showRateCol ? { contract: { label: "Úvazek", icon: FileSignature, key: (u) => u.contract || "HPP", name: (k) => contractOf(k).label } } : {}),
  }), [showRateCol]);
  const GS = useGroupSort(F.visible, { storageKey: "team_order", legacyKey: "team_sort", groups: teamGroups, orders: TEAM_ORDERS, def: { g: "none", gd: 0, k: "name", dir: "asc" }, tie: byName });
  const visible = GS.sorted;
  const effView = narrow ? "cards" : view;

  const noneMembers = useMemo(() => team.filter((u) => inviteState(u) === "none"), [team]);
  const sendable = useMemo(() => noneMembers.filter((u) => u.email), [noneMembers]);
  const noEmailCount = noneMembers.length - sendable.length;

  const copyLink = (url) => navigator.clipboard?.writeText(url).then(() => toast.success("Odkaz zkopírován.")).catch(() => {});

  const invite = async () => {
    if (!form.name.trim() || rateMissing) return;
    try {
      const { data } = await api.post("/team/invite", {
        name: form.name.trim(), email: form.email.trim() || null, phone: form.phone.trim() || null,
        role: form.role, position: form.position.trim() || null,
        hourly_rate: form.hourly_rate ? Number(form.hourly_rate) : null,
        ...(canManage && showRate ? { contract: form.contract } : {}),     // 281: HPP / DPP / na IČO
        send_email: form.send_email,
      });
      const email = form.email, sendNow = form.send_email;
      setOpen(false); setForm({ name: "", email: "", role: "WORKER", phone: "", position: "", hourly_rate: "", contract: "HPP", send_email: false });
      load();
      if (sendNow) setLinkDialog({ open: true, url: data.invite_link, email, uid: data.user_id, sent: data.email_sent });
      else toast.success("Člen založen. Pozvánku můžete odeslat později.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const showPosition = ROLES_WITH_POSITION.includes(form.role);
  const showRate = form.role !== "SUBCONTRACTOR";
  // 240: u nového člena je hodinová sazba povinná — z ní se počítá cena práce na zakázkách.
  // Subdodavatel sazbu nemá (placen od úkolu). Server ji dál bere jako nepovinnou (AI konektor ji nenastavuje).
  const rateMissing = showRate && !(Number(form.hourly_rate) > 0);
  const setRole = (value) => setForm((f) => ({ ...f, role: value, position: ROLES_WITH_POSITION.includes(value) ? f.position : "" }));

  const resend = async (e, u) => {
    e.stopPropagation();
    try {
      const { data } = await api.post(`/team/${u.id}/resend-invite`);
      load();
      setLinkDialog({ open: true, url: data.invite_link, email: u.email, uid: u.id, sent: true });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const sendInviteEmail = async () => {
    try {
      const { data } = await api.post(`/team/${linkDialog.uid}/resend-invite`);
      load();
      setLinkDialog((d) => ({ ...d, url: data.invite_link, sent: true }));
      toast.success("Pozvánka odeslána e-mailem.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const inviteAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/team/invite-all");
      load();
      toast.success(`Odesláno ${plur(data.sent, "pozvánka", "pozvánky", "pozvánek")}.`);
      if (data.skipped?.length) toast.warning(`Bez e-mailu zůstává: ${data.skipped.join(", ")} — doplňte ho v profilu.`);
      if (data.failed?.length) toast.error(`Nepodařilo se odeslat: ${data.failed.join(", ")}.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const ResendBtn = ({ u, icon }) => {
    const st = inviteState(u);
    if (st !== "none" && st !== "invited") return null;
    const noMail = !u.email;
    return (
      <Button variant="outline" size={icon ? "icon" : "sm"} disabled={noMail}
        onClick={(e) => resend(e, u)} data-testid={`resend-${u.id}`}
        title={noMail ? "Nejdřív doplňte e-mail v profilu." : (st === "invited" ? "Poslat znovu" : "Poslat pozvánku")}
        className={icon ? "h-9 w-9 shrink-0" : "h-8 mt-2 w-full sm:w-auto"}>
        <Send size={icon ? 15 : 13} />{icon ? "" : (st === "invited" ? " Poslat znovu" : " Poslat pozvánku")}
      </Button>
    );
  };

  return (
    <div className="space-y-5" data-testid="team-page">
      <ListHeader title="Tým" mobile={mobile} testid="team-header"
        summary={team.length ? <>{plur(kpi.total, "člen", "členové", "členů")}{canManage && kpi.none > 0 ? <> · <span className="pp-ink font-semibold">{kpi.none} bez pozvánky</span></> : null}</> : null}
        search={q} onSearch={setQ} searchPh="Hledat člena, roli, e-mail…"
        primary={canInvite ? { label: "Pozvat", full: "Pozvat člena", icon: UserPlus, onClick: () => setOpen(true), testid: "invite-btn" } : null} />
      <div className="contents">
        {canInvite && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Pozvat člena týmu</DialogTitle>
                <DialogDescription>{showRate ? "Povinné je jméno a hodinová sazba, zbytek můžete doplnit později." : "Povinné je jen jméno, zbytek můžete doplnit později."}</DialogDescription>
              </DialogHeader>

              <div className="space-y-3.5">
                <FormSection icon={User} title="Osobní údaje">
                  <Label className="text-[13px] text-muted-foreground">Jméno a příjmení <span className="text-destructive">*</span></Label>
                  <Input data-testid="invite-name" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="např. Martin Sedlák" className="h-11 mt-1.5 bg-card" />
                </FormSection>

                <FormSection icon={Mail} title="Kontaktní údaje">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="min-w-0">
                      <Label className="text-[13px] text-muted-foreground">E-mail</Label>
                      <Input data-testid="invite-email" type="email" value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value, send_email: e.target.value.trim() ? form.send_email : false })}
                        placeholder="jmeno@firma.cz" className="h-11 mt-1.5 bg-card" />
                    </div>
                    <div className="min-w-0">
                      <Label className="text-[13px] text-muted-foreground">Telefon</Label>
                      <Input data-testid="invite-phone" value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="+420" className="h-11 mt-1.5 bg-card" />
                    </div>
                  </div>
                  <p className="text-[13px] text-muted-foreground mt-2 leading-snug">
                    Bez e-mailu se člen nepřihlásí — hodí se u dělníků, které vedete jen kvůli docházce. Doplnit jde kdykoli.
                  </p>
                </FormSection>

                <FormSection icon={Briefcase} title="Zařazení a sazba">
                  <div className={`grid gap-3 grid-cols-1 ${showPosition && showRate ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
                    <div className="min-w-0">
                      <Label className="text-[13px] text-muted-foreground">Role <span className="text-destructive">*</span></Label>
                      <Select value={form.role} onValueChange={setRole}>
                        <SelectTrigger className="h-11 mt-1.5 bg-card" data-testid="invite-role"><SelectValue /></SelectTrigger>
                        <SelectContent>{roleOptions.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    {showPosition && (
                      <div className="min-w-0">
                        <Label className="text-[13px] text-muted-foreground">Pozice</Label>
                        <Input data-testid="invite-position" value={form.position}
                          onChange={(e) => setForm({ ...form, position: e.target.value })}
                          placeholder={form.role === "SUBCONTRACTOR" ? "např. Elektrikář" : "např. Zedník"}
                          className="h-11 mt-1.5 bg-card" />
                      </div>
                    )}
                    {showRate && (
                      <div className="min-w-0">
                        <Label className="text-[13px] text-muted-foreground">{canManage ? contractOf(form.contract).rateLabel : "Hodinová sazba"} <span className="text-destructive">*</span></Label>
                        <div className={`h-11 mt-1.5 flex items-center gap-2 px-3 bg-card border rounded-[14px] ${form.name.trim() && rateMissing ? "border-[hsl(var(--accent-orange))]" : "border-border"}`}>
                          <input type="number" data-testid="invite-rate" value={form.hourly_rate}
                            onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })}
                            min="0" inputMode="decimal" placeholder="0" className="flex-1 min-w-0 bg-transparent outline-none text-[15px]" />
                          <span className="text-[13px] font-semibold text-muted-foreground shrink-0">{currencySymbol()}/h</span>
                        </div>
                      </div>
                    )}
                  </div>
                  {canManage && showRate && (
                    <div className="mt-3" data-testid="invite-contract-row">
                      <Label className="text-[13px] text-muted-foreground">Typ spolupráce</Label>
                      <ContractPicker value={form.contract} rate={form.hourly_rate} onChange={(v) => setForm({ ...form, contract: v })}
                        testid="invite-contract" className="mt-1.5" />
                    </div>
                  )}
                  <p className="text-[13px] text-muted-foreground mt-2.5 leading-snug">{ROLE_HINT[form.role]}</p>
                </FormSection>

                <FormSection icon={Send} title="Pozvánka">
                  <label className={`flex items-start gap-2.5 ${form.email.trim() ? "cursor-pointer" : "opacity-60 cursor-not-allowed"}`}>
                    <input type="checkbox" data-testid="invite-send-email" checked={form.send_email} disabled={!form.email.trim()}
                      onChange={(e) => setForm({ ...form, send_email: e.target.checked })}
                      className="w-5 h-5 mt-0.5 shrink-0 accent-[hsl(var(--accent-orange))] cursor-pointer" />
                    <span>
                      <span className="block text-[15px] font-semibold">Rovnou odeslat pozvánku e-mailem</span>
                      <span className="block text-[13px] text-muted-foreground mt-0.5 leading-snug">
                        {!form.email.trim() ? "Nejdřív zadejte e-mail, pak půjde pozvánku rovnou odeslat."
                          : form.send_email ? "Členovi přijde e-mail s odkazem, na kterém si nastaví heslo."
                          : "Člen se jen založí interně — žádný e-mail mu nepřijde. Pozvánku můžete odeslat kdykoli později."}
                      </span>
                    </span>
                  </label>
                </FormSection>

                {form.name.trim() && rateMissing && (
                  <p className="text-[13px] text-muted-foreground leading-snug" data-testid="invite-rate-hint">
                    Doplňte hodinovou sazbu — z ní se počítá cena práce na zakázkách.
                  </p>
                )}
                <Button onClick={invite} data-testid="invite-send" disabled={!form.name.trim() || rateMissing} className="w-full h-12 font-bold">
                  {form.send_email ? "Vytvořit a pozvat" : "Vytvořit člena"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <ListToolbar mobile={mobile} testid="team-filters"
        tabs={canManage ? [
          { value: "all", label: "Všichni", count: kpi.total }, { value: "none", label: "Bez pozvánky", count: kpi.none },
          { value: "invited", label: "Pozvaní", count: kpi.invited }, { value: "inactive", label: "Neaktivní", count: kpi.inactive },
        ] : undefined}
        tab={stateF} onTab={setStateF} filter={F} search={q} onSearch={setQ} searchPh="Hledat člena, roli, e-mail…"
        sort={GS.sortProp}
        views={narrow ? undefined : [{ value: "table", label: "Tabulka", icon: Table2, testid: "view-tab-table" }, { value: "cards", label: "Karty", icon: LayoutGrid, testid: "view-tab-cards" }]}
        view={effView} onView={setView} />

      {/* Pruh „ještě nemají pozvánku" */}
      {canManage && kpi.none > 0 && (
        <div className="flex items-center gap-3 flex-wrap p-3 sm:px-4 rounded-[14px] bg-sky-500/10 border border-sky-500/30" data-testid="invite-all-banner">
          <span className="text-sm font-semibold">
            {plur(kpi.none, "člověk", "lidi", "lidí")} ještě nemá pozvánku
            {noEmailCount ? ` — u ${noEmailCount} z nich chybí e-mail.` : " — bez ní se do aplikace nepřihlásí."}
          </span>
          <Button variant="outline" onClick={inviteAll} disabled={!sendable.length || busy}
            data-testid="invite-all" className="w-full sm:w-auto sm:ml-auto h-10 font-bold">
            <Send size={15} /> {sendable.length ? `Poslat ${plur(sendable.length, "pozvánku", "pozvánky", "pozvánek")}` : "Nelze odeslat — chybí e-maily"}
          </Button>
        </div>
      )}

      {/* Prázdné stavy */}
      {team.length === 0 ? (
        <div className="p-6 text-center text-muted-foreground bg-card border border-border rounded-[20px]">Zatím žádní členové týmu.</div>
      ) : visible.length === 0 ? (
        <div className="p-6 text-center text-muted-foreground bg-card border border-border rounded-[20px]">Nikdo neodpovídá filtru.</div>
      ) : effView === "cards" ? (
        <GroupedGrid groups={GS.groups} rows={visible} closed={GS.closed} onToggle={GS.toggle} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {(u) => {
            const st = inviteState(u);
            return (
              <div key={u.id} role="button" tabIndex={0} data-testid={`team-member-${u.id}`}
                onClick={() => nav(`/tym/${u.id}`)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); nav(`/tym/${u.id}`); } }}
                className={`bg-card border border-border rounded-[20px] p-4 flex items-center gap-4 cursor-pointer hover:border-brand transition-colors ${st === "inactive" ? "opacity-70" : st === "none" ? "bg-sky-500/[0.06]" : ""}`}>
                <Avatar name={u.name} photo={u.photo_url} size={48} className="bg-brand/10 text-brand" testid={`team-avatar-${u.id}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-bold flex items-center gap-2 min-w-0">
                    <span className="truncate">{u.name}</span>
                    <StatePill u={u} testid={`state-pill-${u.id}`} />
                    {u.docs_warning && (u.docs_warning.expired || u.docs_warning.expiring) > 0 && (
                      <span data-testid={`team-docs-warning-${u.id}`} title={docsWarnTitle(u.docs_warning)}
                        className={`shrink-0 inline-flex items-center ${u.docs_warning.expired ? "text-destructive" : "text-amber-500"}`}><AlertTriangle size={15} /></span>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground truncate mt-0.5">{ROLE_LABELS[u.role]}{u.position ? ` · ${u.position}` : ""}</div>
                  {!u.email && <div className="text-[13px] text-destructive font-semibold mt-0.5">chybí e-mail</div>}
                  <ResendBtn u={u} />
                </div>
              </div>
            );
          }}
        </GroupedGrid>
      ) : (
        <div className="bg-card border border-border rounded-[20px] overflow-hidden overflow-x-auto" data-testid="team-table">
          <div className={showRateCol ? "min-w-[880px]" : "min-w-[780px]"}>
            <div className={`grid ${showRateCol ? "grid-cols-[2fr_1.4fr_1.7fr_110px_150px_52px]" : "grid-cols-[2fr_1.4fr_1.7fr_150px_52px]"} items-center gap-3 px-4 bg-secondary/60 border-b border-border`}>
              {["Člen", "Role a pozice", "Kontakt", ...(showRateCol ? ["Sazba"] : []), "Stav", ""].map((h, i) => (
                <span key={i} className="min-h-[46px] flex items-center text-[12px] font-bold uppercase tracking-[0.08em] text-muted-foreground">{h}</span>
              ))}
            </div>
            {(() => { const rowEl = (u) => {
              const st = inviteState(u);
              return (
                <div key={u.id} role="button" tabIndex={0} data-testid={`team-member-${u.id}`}
                  onClick={() => nav(`/tym/${u.id}`)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); nav(`/tym/${u.id}`); } }}
                  className={`w-full text-left grid ${showRateCol ? "grid-cols-[2fr_1.4fr_1.7fr_110px_150px_52px]" : "grid-cols-[2fr_1.4fr_1.7fr_150px_52px]"} items-center gap-3 px-4 py-3 border-b border-border cursor-pointer hover:bg-secondary/50 transition-colors ${st === "inactive" ? "opacity-70" : st === "none" ? "bg-sky-500/[0.06]" : ""}`}>
                  <span className="flex items-center gap-3 min-w-0">
                    <Avatar name={u.name} photo={u.photo_url} size={40} className="bg-brand/10 text-brand shrink-0" testid={`team-avatar-${u.id}`} />
                    <span className="min-w-0">
                      <span className="font-bold text-[15px] truncate flex items-center gap-1.5">
                        {u.name}
                        {u.docs_warning && (u.docs_warning.expired || u.docs_warning.expiring) > 0 && (
                          <span data-testid={`team-docs-warning-${u.id}`} title={docsWarnTitle(u.docs_warning)}
                            className={`shrink-0 inline-flex items-center ${u.docs_warning.expired ? "text-destructive" : "text-amber-500"}`}><AlertTriangle size={14} /></span>
                        )}
                      </span>
                    </span>
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm truncate">{ROLE_LABELS[u.role]}{u.position ? ` · ${u.position}` : ""}</span>
                    {canManage && <span className="block text-xs text-muted-foreground truncate">Naposledy: {u.last_login ? formatDateTime(u.last_login) : "nikdy"}</span>}
                  </span>
                  <span className="min-w-0 text-sm">
                    {u.email ? <span className="block truncate">{u.email}</span> : <span className="block text-destructive font-semibold">chybí e-mail</span>}
                    {u.phone && <span className="block text-muted-foreground truncate">{u.phone}</span>}
                  </span>
                  {showRateCol && <span className="text-sm tabular-nums text-right">{u.hourly_rate != null ? `${formatCZK(u.hourly_rate)}/h` : "—"}
                    {u.contract && u.contract !== "HPP" && <span className="block text-xs text-muted-foreground">{contractOf(u.contract).label}</span>}</span>}
                  <span><StatePill u={u} testid={`state-pill-${u.id}`} /></span>
                  <span className="justify-self-end" onClick={(e) => e.stopPropagation()}><ResendBtn u={u} icon /></span>
                </div>
              );
            };
            // 378: se skupinami lišta skupiny nad jejími řádky
            return GS.groups ? GS.groups.map((g) => [<GroupHeader key={`g-${g.key}`} g={g} closed={GS.closed.has(g.key)} onToggle={GS.toggle} />, ...(GS.closed.has(g.key) ? [] : g.rows.map(rowEl))]) : visible.map(rowEl);
            })()}
          </div>
        </div>
      )}

      {/* Invite link dialog */}
      <Dialog open={linkDialog.open} onOpenChange={(o) => setLinkDialog((d) => ({ ...d, open: o }))}>
        <DialogContent data-testid="invite-link-dialog">
          <DialogHeader><DialogTitle>{linkDialog.sent ? "Pozvánka odeslána" : "Člen založen"}</DialogTitle><DialogDescription className="sr-only">Odkaz na pozvánku</DialogDescription></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {linkDialog.sent ? (<>
              E-mail s pozvánkou jsme odeslali na <b>{linkDialog.email}</b>. Pokud nedorazí (zkontrolujte spam),
              zkopírujte a pošlete tento odkaz ručně (např. přes SMS nebo WhatsApp). Zaměstnanec si na něm nastaví heslo.
            </>) : (<>
              Účet je zatím jen interní — na <b>{linkDialog.email}</b> jsme nic neposílali. Až budete chtít zaměstnance
              přizvat, odešlete pozvánku tlačítkem níže, nebo mu předejte odkaz ručně (SMS, WhatsApp). Nastaví si na něm heslo.
            </>)}
          </p>
          <div className="flex items-center gap-2 p-2 bg-secondary rounded-sm">
            <Link2 size={16} className="text-muted-foreground shrink-0" />
            <input readOnly value={linkDialog.url} data-testid="invite-link-input" className="flex-1 bg-transparent text-sm outline-none min-w-0" />
            <Button size="sm" onClick={() => copyLink(linkDialog.url)} data-testid="copy-link-btn" className="h-9"><Copy size={14} /> Kopírovat</Button>
          </div>
          {!linkDialog.sent && (
            <Button onClick={sendInviteEmail} data-testid="send-invite-email-btn" className="w-full md:w-auto md:self-end h-11 font-bold">
              <Send size={15} /> Odeslat pozvánku e-mailem
            </Button>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
